import * as React from 'react';
import type { ReactNode } from 'react';
import { Root } from '@/root';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';

import { routes } from '@/routes';
import { ScrollRestoration } from '@/components/core/v2/scroll-restoration';
import ErrorBoundaryFallback from '@/components/error-boundary-fallback';

import { config } from './config';

// Preload critical logo images immediately when app starts
const preloadLogoImages = () => {
  const logoImages = ['/src/assets/logos/hoaone-logo-light.png', '/src/assets/logos/hoaone-logo-dark.png'];

  logoImages.forEach((src) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};

// Execute preloading as soon as possible
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preloadLogoImages);
  } else {
    preloadLogoImages();
  }
}

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: config.sentry.dsn,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),

      // send console.log, console.warn, and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.hoaone\.io/,
      /^https:\/\/app\.hoaone\.io/,
      /^https:\/\/api\.gcs-demo\.swantech\.io/,
      /^https:\/\/app\.gcs-demo\.swantech\.io/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Enable logs to be sent to Sentry
    enableLogs: true,
  });
}

const root = createRoot(document.getElementById('root')!);

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Root>
        <ScrollRestoration />
        <Outlet />
      </Root>
    ),
    children: [...routes],
  },
]);

const app: ReactNode = (
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorBoundaryFallback error={error as Error} resetErrorBoundary={resetError} />
      )}
      onError={(error) => {
        if (window.newrelic?.noticeError) {
          window.newrelic.noticeError(error);
        }
      }}
    >
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

root.render(app as any);
